import { PageBody } from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';
import { ErrorState } from '@pypestream/ui-patterns';
import { FC } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { isOutsideOrgPath } from '../../utils/org-path-prefix';
import { useManagerCtxSelector } from '../../xstate/app.xstate';

/**
 * The page for an address no route matches. Without it the router answers with
 * its own default error screen ("Unexpected Application Error! 404 Not Found",
 * addressed to a developer).
 *
 * `routes.home` is `/` until an org is selected and the selected org's page
 * after, so the action leads somewhere real on a first load as well.
 *
 * An address outside `/organization/…` is about to be moved under the selected
 * org by `RouteDataProvider`, and may well land on a real page (`/users` ->
 * `/organization/<id>/users`). Showing the error screen for the moment before
 * that redirect flashed it at people who then got the right page, and took
 * their focus. Nothing is drawn until the address has settled.
 * LIMITATION: if no org is ever selected the redirect never runs and this
 * stays empty; the app has no usable state without a selected org anyway.
 */
export const NotFoundPage: FC = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const home = useManagerCtxSelector((ctx) => ctx.routes.home);

  if (isOutsideOrgPath(pathname)) return null;

  return (
    <PageBody background="none">
      <ErrorState
        testId="page-not-found"
        label={t('manager/common:errorBoundary.pageNotFound') || ''}
        body={t('manager/common:notFound.body') || ''}
        actionTestId="page-not-found-home-btn"
        actionLabel={t('manager/common:errorBoundary.goHome') || ''}
        onAction={() => navigate(home)}
      />
    </PageBody>
  );
};
